import { useEffect, useState } from "react";

import styles from "@styles/home-configurations.module.scss";
import { MiniCheck, MiniInfo, SuccessIcon } from "shared/components/Icons";
import { Button, Input, Radio } from "@components";
import { If } from "shared/components/control-statements";
import Availability from "./Availability";

import LayoutDefault from "../../../assets/layout-brands-default.png";
import LayoutGrid from "../../../assets/layout-brands-grid.png";

import classNames from "classnames/bind";
const css = classNames.bind(styles);

const LAYOUT_OPTIONS = [
  { key: "DEFAULT", label: "Padrão", image: LayoutDefault },
  {
    key: "VERTICAL_SCROLL",
    label: "Listagem",
    image: LayoutGrid,
  },
];

const BrandsConfig = ({ data, brands, onClose, onSave, onFinish }) => {
  const [steps] = useState([
    { key: "TYPE", label: "Tipo da seção", show: true },
    { key: "LAYOUT", label: "Preenchimento", show: true },
    { key: "TITLE", label: "Título da seção", show: true },
    {
      key: "SORT",
      label: "Ordenar marcas",
      show: true,
    },
    { key: "AVAILABILITY", label: "Configurar disponibilidade", show: true },
    { key: "OVERVIEW", label: "Resumo", show: true },
  ]);

  const [configBrands, setConfigBrands] = useState({
    type: "BRANDS",
    layout: data?.layout || null,
    title: data?.title || null,
    items: data?.items || [],
    availability: data?.availability || null,
  });

  const [selectedStep, setSelectedStep] = useState(null);
  const [completedSteps, setCompletedSteps] = useState(["TYPE"]);
  const [showSuccess, setShowSuccess] = useState(false);

  const brandMap = new Map(brands.map((brand) => [brand.id, brand]));

  useEffect(() => {
    const loadBrands = async () => {
      const sortedBrands = (data?.items || []).filter((brandId) =>
        brandMap.has(brandId)
      );
      const newBrands = brands.filter(
        (brand) => sortedBrands.includes(brand.id) === false
      );
      const mergedBrands = [
        ...sortedBrands,
        ...newBrands.map((brand) => brand.id),
      ];

      setConfigBrands((prev) => ({
        ...prev,
        items: mergedBrands,
      }));
    };

    const getInitialSelectedStep = () => {
      const stepsWithoutType = completedSteps.filter((step) => step !== "TYPE");
      return stepsWithoutType.at(-1) || "LAYOUT";
    };

    loadBrands();

    setSelectedStep(getInitialSelectedStep);
  }, [brands]);

  const handleConfig = (key, value) => {
    setConfigBrands({ ...configBrands, [key]: value });
  };

  const getVisibleSteps = () => steps.filter((step) => step.show);

  const handleContinue = () => {
    if (selectedStep === "OVERVIEW") {
      onSave(configBrands);
      setShowSuccess(true);
      return;
    }

    setCompletedSteps((prev) =>
      prev.includes(selectedStep) ? prev : [...prev, selectedStep]
    );

    const visibleSteps = getVisibleSteps();
    const currentIndex = visibleSteps.findIndex(
      (step) => step.key === selectedStep
    );

    const nextStep = visibleSteps[currentIndex + 1];

    if (nextStep) setSelectedStep(nextStep.key);
  };

  const validateStep = () => {
    switch (selectedStep) {
      case "LAYOUT":
        return configBrands.layout !== null;
      case "TITLE":
        return configBrands.title !== null;
      case "SORT":
        return configBrands.items !== null;
      case "AVAILABILITY":
        return (
          configBrands.availability?.length > 0 ||
          configBrands.availability === null
        );
      case "OVERVIEW":
        return true;
      default:
        return false;
    }
  };

  const isValidStep = validateStep();

  const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  const formatHour = (hour) => {
    const h = hour.padStart(4, "0").slice(0, 2);
    const m = hour.padStart(4, "0").slice(2);
    return `${h}:${m}`;
  };

  const Horaries = () => {
    if (configBrands.availability === null) return <span>A todo momento</span>;

    return (
      <div>
        {configBrands.availability.map((entry, index) => {
          const day = weekDays[entry.weekDay];
          const horaries = entry.times.map((t, idx) => (
            <span key={idx}>
              {formatHour(t.start)} - {formatHour(t.end)}
              {idx < entry.times.length - 1 && " / "}
            </span>
          ));

          return (
            <span key={index} className={styles.time}>
              <strong>{day}:</strong> {horaries}
              {index < configBrands.availability.length - 1 && " | "}
            </span>
          );
        })}
      </div>
    );
  };

  const moveItem = (index, direction) => {
    const newIndex = index + direction;

    if (newIndex < 0 || newIndex >= (configBrands.items || []).length) return;

    const newItems = [...configBrands.items];
    const temp = newItems[index];
    newItems[index] = newItems[newIndex];
    newItems[newIndex] = temp;

    setConfigBrands({ ...configBrands, items: newItems });
  };

  const builtBrands = configBrands.items.map((brandId) => {
    const brand = brandMap.get(brandId);
    return {
      id: brand.id,
      name: brand.name,
      logo: brand.logo,
    };
  });

  return (
    <>
      <If cond={!showSuccess}>
        <div className={styles.homeSidebar}>
          <ul>
            {steps
              .filter(({ show }) => show)
              .map(({ key, label }) => {
                const isSelected = selectedStep === key;
                const isCompleted = completedSteps.includes(key);

                return (
                  <li
                    key={String(key)}
                    className={css({ selected: isSelected })}
                    onClick={() => {
                      if (key === "TYPE") return onClose();
                      if (isCompleted) setSelectedStep(key);
                    }}
                  >
                    {isCompleted ? <MiniCheck /> : <MiniInfo />}
                    <span>{label}</span>
                  </li>
                );
              })}
          </ul>
        </div>
      </If>

      <If cond={!showSuccess}>
        <div className={styles.scrollContainer}>
          <If cond={selectedStep === "LAYOUT"}>
            <div className={styles.configContainer}>
              <p className={styles.title}>Defina o preenchimento</p>
              <div className={styles.options}>
                {LAYOUT_OPTIONS.map((option) => (
                  <div
                    className={styles.option}
                    key={option.key}
                    onClick={() => handleConfig("layout", option.key)}
                  >
                    <div
                      className={css({
                        box: true,
                        selected: configBrands.layout === option.key,
                      })}
                    >
                      <div className={styles.image}>
                        <img src={option.image} alt={option.label} />
                      </div>
                    </div>

                    <Radio
                      checked={configBrands.layout}
                      value={option.key}
                      readOnly
                      family="layout"
                    >
                      {option.label}
                    </Radio>
                  </div>
                ))}
              </div>
            </div>
          </If>

          <If cond={selectedStep === "TITLE"}>
            <div className={styles.configContainer}>
              <p className={styles.title}>Nome da seção</p>
              <Input
                className="mt-15"
                autoFocus
                value={configBrands.title}
                onChange={(e) => handleConfig("title", e.target.value)}
              />
            </div>
          </If>

          <If cond={selectedStep === "SORT"}>
            <div className={styles.configContainer}>
              <p className={styles.title}>Lojas ({builtBrands.length})</p>
              <div className={styles.brands}>
                <ul>
                  {(builtBrands || []).map((item, index) => (
                    <li key={item.id}>
                      <div className="d-flex align-center gap-10">
                        <button
                          onClick={() => moveItem(index, 1)}
                          disabled={index === builtBrands.length - 1}
                          type="button"
                          className={styles.moveDown}
                        >
                          <i className="fas fa-chevron-down" />
                        </button>
                        <button
                          onClick={() => moveItem(index, -1)}
                          disabled={index === 0}
                          type="button"
                          className={styles.moveUp}
                        >
                          <i className="fas fa-chevron-up" />
                        </button>
                      </div>
                      <div className="d-flex align-center">
                        <div className={styles.logo}>
                          {item.logo ? (
                            <img src={item.logo} alt={item.name} />
                          ) : null}
                        </div>
                        <span className={styles.name}>{item.name}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </If>

          <If cond={selectedStep === "AVAILABILITY"}>
            <Availability
              availability={configBrands.availability}
              onChange={(availability) =>
                handleConfig("availability", availability)
              }
            />
          </If>

          <If cond={selectedStep === "OVERVIEW"}>
            <div className={styles.configContainer}>
              <p className={styles.title}>Resumo</p>

              <ul className={styles.resume}>
                <li>
                  <p>Tipo da seção</p>
                  <span>Banners</span>
                </li>
                <li>
                  <p>Preenchimento</p>
                  <span>
                    {
                      LAYOUT_OPTIONS.find((o) => o.key === configBrands.layout)
                        ?.label
                    }
                  </span>
                </li>
                <li>
                  <p>Título da seção</p>
                  <span>{configBrands.title}</span>
                </li>
                <li>
                  <p>Disponibilidade</p>
                  <Horaries />
                </li>
                <li>
                  <p>Lojas ({builtBrands.length})</p>
                  <div className={styles.orderedBrands}>
                    <ul style={{ listStyle: "none", padding: 0 }}>
                      {(builtBrands || []).map((item, index) => (
                        <li key={item.id}>
                          <div className="d-flex align-center">
                            <div className={styles.logo}>
                              {item.logo ? (
                                <img src={item.logo} alt={item.name} />
                              ) : null}
                            </div>
                            <span className={styles.name}>{item.name}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              </ul>
            </div>
          </If>
        </div>

        <div className={styles.footer}>
          <Button
            design="primary"
            onClick={handleContinue}
            disabled={!isValidStep}
          >
            {selectedStep === "OVERVIEW" ? "Confirmar e finalizar" : "Próximo"}
          </Button>
        </div>
      </If>

      <If cond={showSuccess}>
        <div className={styles.successContainer}>
          <div className={styles.successIcon}>
            <SuccessIcon size={14} />
          </div>
          <p className={styles.successMessage}>
            Seção {data?.id ? "atualizada" : "criada"} com sucesso.
          </p>

          <div className={styles.footer}>
            <Button design="primary" onClick={onFinish}>
              Fechar
            </Button>
          </div>
        </div>
      </If>
    </>
  );
};

export default BrandsConfig;
